import React, { useState } from 'react';
import {
    Plus,
    X,
    Edit3,
    Copy,
    Trash2,
    CheckCircle,
    Star,
    Archive,
    Download,
    Upload,
    Settings,
    LucideIcon
} from 'lucide-react';
import {useTranslation} from "react-i18next";

// Type definitions matching SmartList component
interface Category {
    id: string;
    name: string;
    color: string;
    textColor: string;
}

interface ActionConfig {
    id: string;
    label: string;
    icon: string;
    action: string;
    dangerous?: boolean;
}

interface SmartListConfig {
    title: string;
    placeholder: string;
    categories: Category[];
    listActions: ActionConfig[];
    itemActions: ActionConfig[];
    showCategories: boolean;
    showReorder: boolean;
}

interface SmartListSettingsProps {
    config: SmartListConfig;
    onChange: (config: SmartListConfig) => void;
    className?: string;
}

// Available icons for actions
export const availableIcons: { [key: string]: LucideIcon } = {
    'edit': Edit3,
    'copy': Copy,
    'trash': Trash2,
    'check': CheckCircle,
    'star': Star,
    'archive': Archive,
    'download': Download,
    'upload': Upload,
    'settings': Settings,
    'plus': Plus,
    'x': X
};

// Predefined color options
const colorOptions = [
    { bg: 'bg-red-200', text: 'text-red-800', name: 'Red' },
    { bg: 'bg-orange-200', text: 'text-orange-800', name: 'Orange' },
    { bg: 'bg-yellow-200', text: 'text-yellow-800', name: 'Yellow' },
    { bg: 'bg-green-200', text: 'text-green-800', name: 'Green' },
    { bg: 'bg-primary/20', text: 'text-primary', name: 'Blue' },
    { bg: 'bg-purple-200', text: 'text-purple-800', name: 'Purple' },
    { bg: 'bg-pink-200', text: 'text-pink-800', name: 'Pink' },
    { bg: 'bg-gray-200', text: 'text-gray-800', name: 'Gray' }
];

const SmartListSettings: React.FC<SmartListSettingsProps> = ({
                                                                 config,
                                                                 onChange,
                                                                 className = "",
                                                             }) => {
    const [newCategoryName, setNewCategoryName] = useState<string>('');
    const [newCategoryColor, setNewCategoryColor] = useState<number>(0);
    const [newActionLabel, setNewActionLabel] = useState<string>('');
    const [newActionIcon, setNewActionIcon] = useState<string>('edit');
    const [editingAction, setEditingAction] = useState<{ type: 'list' | 'item', index: number } | null>(null);

    const {t} = useTranslation();

    const updateConfig = (updates: Partial<SmartListConfig>): void => {
        onChange({ ...config, ...updates });
    };

    // Category management
    const addCategory = (): void => {
        if (!newCategoryName.trim()) return;

        const colorOption = colorOptions[newCategoryColor];
        const newCategory: Category = {
            id: newCategoryName.toLowerCase().replace(/\s+/g, '-'),
            name: newCategoryName.trim(),
            color: colorOption.bg,
            textColor: colorOption.text
        };

        updateConfig({
            categories: [...config.categories, newCategory]
        });

        setNewCategoryName('');
    };

    const removeCategory = (index: number): void => {
        const newCategories = config.categories.filter((_, i) => i !== index);
        updateConfig({ categories: newCategories });
    };

    // Action management
    const addAction = (type: 'list' | 'item'): void => {
        if (!newActionLabel.trim()) return;

        const newAction: ActionConfig = {
            id: newActionLabel.toLowerCase().replace(/\s+/g, '-'),
            label: newActionLabel.trim(),
            icon: newActionIcon,
            action:'',
            dangerous: false
        };

        const key = type === 'list' ? 'listActions' : 'itemActions';
        updateConfig({
            [key]: [...config[key], newAction]
        });

        setNewActionLabel('');
    };

    const removeAction = (type: 'list' | 'item', index: number): void => {
        const key = type === 'list' ? 'listActions' : 'itemActions';
        const newActions = config[key].filter((_, i) => i !== index);
        updateConfig({ [key]: newActions });
    };

    const toggleActionDangerous = (type: 'list' | 'item', index: number): void => {
        const key = type === 'list' ? 'listActions' : 'itemActions';
        const newActions = [...config[key]];
        newActions[index] = { ...newActions[index], dangerous: !newActions[index].dangerous };
        updateConfig({ [key]: newActions });
    };

    return (
        <div className={`p-1 mt-2 space-y-6 text-sm ${className}`}>
            {/* Basic Settings */}
            <div className="space-y-3">
                <h3 className="text-base font-medium text-gray-800 border-b pb-1">{t('basic_settings')}</h3>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('title')}</label>
                    <input
                        type="text"
                        value={config.title}
                        onChange={(e) => updateConfig({ title: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-primary/40 focus:ring-1 focus:ring-primary/10"
                        placeholder="List title"
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('add_item_placeholder')}</label>
                    <input
                        type="text"
                        value={config.placeholder}
                        onChange={(e) => updateConfig({ placeholder: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-primary/40 focus:ring-1 focus:ring-primary/10"
                        placeholder="Add an item..."
                    />
                </div>

                <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                            type="checkbox"
                            checked={config.showCategories}
                            onChange={(e) => updateConfig({ showCategories: e.target.checked })}
                            className="rounded"
                        />
                        {t('show_categories')}
                    </label>

                    <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                            type="checkbox"
                            checked={config.showReorder}
                            onChange={(e) => updateConfig({ showReorder: e.target.checked })}
                            className="rounded"
                        />
                        {t('allow_reorder')}
                    </label>
                </div>
            </div>

            {/* Categories */}
            <div className="space-y-3">
                <h3 className="text-base font-medium text-gray-800 border-b pb-1">{t('categories')}</h3>

                {/* Add Category */}
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="Category name"
                            className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:border-primary/40 focus:ring-1 focus:ring-primary/10"
                        />
                        <select
                            value={newCategoryColor}
                            onChange={(e) => setNewCategoryColor(parseInt(e.target.value))}
                            className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:border-primary/40"
                        >
                            {colorOptions.map((color, index) => (
                                <option key={index} value={index}>{color.name}</option>
                            ))}
                        </select>
                        <button
                            onClick={addCategory}
                            className="px-2 py-1.5 bg-primary text-white text-xs rounded hover:bg-primary transition-colors"
                        >
                            <Plus className="w-3 h-3" />
                        </button>
                    </div>
                </div>

                {/* Category List */}
                <div className="space-y-1 max-h-32 overflow-y-auto">
                    {config.categories.map((category, index) => (
                        <div key={category.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <span className={`px-2 py-0.5 text-xs rounded ${category.color} ${category.textColor}`}>
                {category.name}
              </span>
                            <button
                                onClick={() => removeCategory(index)}
                                className="ml-auto p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* List Actions */}
           {/* <div className="space-y-3">
                <h3 className="text-base font-medium text-gray-800 border-b pb-1">List Actions</h3>

                 Add List Action
                <div className="flex gap-2">
                    <input
                        type="text"
                        onChange={(e) => setNewActionLabel(e.target.value)}
                        placeholder="Action label"
                        className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:border-primary/40 focus:ring-1 focus:ring-primary/10"
                    />
                    <select
                        value={newActionIcon}
                        onChange={(e) => setNewActionIcon(e.target.value)}
                        className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:border-primary/40"
                    >
                        {Object.keys(availableIcons).map(iconKey => (
                            <option key={iconKey} value={iconKey}>{iconKey}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => addAction('list')}
                        className="px-2 py-1.5 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                    >
                        <Plus className="w-3 h-3" />
                    </button>
                </div>

                 List Actions List
                <div className="space-y-1 max-h-24 overflow-y-auto">
                    {config.listActions.map((action, index) => (
                        <div key={action.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    {React.createElement(availableIcons[action.icon], { className: "w-3 h-3 text-gray-500" })}
                            <span className="text-xs text-gray-700 flex-1">{action.label}</span>
                            <label className="flex items-center gap-1 text-xs text-gray-600">
                                <input
                                    type="checkbox"
                                    checked={action.dangerous || false}
                                    onChange={() => toggleActionDangerous('list', index)}
                                    className="w-3 h-3"
                                />
                                Dangerous
                            </label>
                            <button
                                onClick={() => removeAction('list', index)}
                                className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>*/}

            {/* Item Actions */}
           {/* <div className="space-y-3">
                <h3 className="text-base font-medium text-gray-800 border-b pb-1">Item Actions</h3>

                 Add Item Action
                <div className="flex gap-2">
                    <input
                        type="text"
                        onChange={(e) => setNewActionLabel(e.target.value)}
                        placeholder="Action label"
                        className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:border-primary/40 focus:ring-1 focus:ring-primary/10"
                    />
                    <select
                        value={newActionIcon}
                        onChange={(e) => setNewActionIcon(e.target.value)}
                        className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:border-primary/40"
                    >
                        {Object.keys(availableIcons).map(iconKey => (
                            <option key={iconKey} value={iconKey}>{iconKey}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => addAction('item')}
                        className="px-2 py-1.5 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                    >
                        <Plus className="w-3 h-3" />
                    </button>
                </div>

                 Item Actions List
                <div className="space-y-1 max-h-24 overflow-y-auto">
                    {config.itemActions.map((action, index) => (
                        <div key={action.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                            {React.createElement(availableIcons[action.icon], { className: "w-3 h-3 text-gray-500" })}
                            <span className="text-xs text-gray-700 flex-1">{action.label}</span>
                            <label className="flex items-center gap-1 text-xs text-gray-600">
                                <input
                                    type="checkbox"
                                    checked={action.dangerous || false}
                                    onChange={() => toggleActionDangerous('item', index)}
                                    className="w-3 h-3"
                                />
                                Dangerous
                            </label>
                            <button
                                onClick={() => removeAction('item', index)}
                                className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>*/}

            {/* Preview */}
            <div className="space-y-2 pt-2 border-t">
                <h3 className="text-xs font-medium text-gray-600">{t('configuration_summary')}</h3>
                <div className="text-xs text-gray-500 space-y-1">
                    <div>{t('categories')}: {config.categories.length}</div>
                    {/*<div>List Actions: {config.listActions.length}</div>*/}
                    {/*<div>Item Actions: {config.itemActions.length}</div>*/}
                    <div>{config.showCategories ? '✓ '+ t('show_categories') :''}  {config.showReorder ? '✓ ' + t('allow_reorder') : ''} </div>
                </div>
            </div>
        </div>
    );
};

export default SmartListSettings;